import { useState, useEffect, useCallback } from "react";
import "./Routing.scss";
import {
  getWhatsappLogsApi,
  getWhatsappLogByIdApi,
  retryWhatsappLogApi,
} from "../../../../../api/api";

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_ICONS = {
  ROUTED: "✓",
  FAILED: "✕",
  REJECTED: "⊘",
  PENDING: "⏳",
  DUPLICATE: "⧉",
  SUCCESS: "✓",
};

const MESSAGE_TYPES = [
  "image",
  "video",
  "audio",
  "pdf",
  "document",
  "text",
  "ptv",
  "excel",
];

const STATUS_CHIPS = ["", "ROUTED", "FAILED", "REJECTED", "SUCCESS"];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatFileSize(bytes) {
  if (!bytes) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const lower = (status || "").toLowerCase();
  const label = status
    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    : "Unknown";
  return (
    <span className={`badge badge--${lower}`}>
      {STATUS_ICONS[status] || "?"} {label}
    </span>
  );
}

function TypeTag({ type }) {
  if (!type) return <span className="td-room--empty">—</span>;
  const lower = type.toLowerCase();
  return (
    <span className={`type-tag type-tag--${lower}`}>{type.toUpperCase()}</span>
  );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────
function LogDetailDrawer({ logId, onClose, onRetry }) {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getWhatsappLogByIdApi(logId)
      .then((res) => setLog(res.data.log))
      .catch(() => setError("Failed to load log details."))
      .finally(() => setLoading(false));
  }, [logId]);

  const handleOverlay = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Build detail rows — only include fields that have real values
  const buildDetailRows = (log) => {
    const rows = [];

    rows.push(["Log ID", <span className="mono-val">{log.id}</span>]);
    rows.push(["Timestamp", formatDate(log.createdAt)]);
    rows.push(["Action", log.action]);
    rows.push(["Entity Type", log.entityType]);

    if (log.whatsappIdentity)
      rows.push(["WhatsApp Identity", log.whatsappIdentity]);

    if (log.whatsappMessageId)
      rows.push([
        "Message ID",
        <span className="mono-val truncate-val">{log.whatsappMessageId}</span>,
      ]);

    if (log.matrixRoomId)
      rows.push([
        "Matrix Room",
        <span className="mono-val truncate-val">{log.matrixRoomId}</span>,
      ]);

    if (log.mappingId)
      rows.push([
        "Mapping ID",
        <span className="mono-val">{log.mappingId}</span>,
      ]);

    if (log.messageType)
      rows.push(["Message Type", <TypeTag type={log.messageType} />]);

    if (log.fileSize) rows.push(["File Size", formatFileSize(log.fileSize)]);

    // Pull extra fields from details object if they add value
    if (log.details?.mappingType)
      rows.push(["Mapping Type", log.details.mappingType]);

    return rows;
  };

  const isError = log?.status === "FAILED" || log?.status === "REJECTED";

  return (
    <div className="drawer-overlay" onClick={handleOverlay}>
      <div className="drawer">
        <div className="drawer__header">
          <span className="drawer__title">Routing Log Detail</span>
          <button className="btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>

        {loading && (
          <div className="drawer__body drawer__body--center">
            <div className="drawer-spinner" />
            <p className="drawer-loading-text">Loading event…</p>
          </div>
        )}

        {!loading && (error || !log) && (
          <>
            <div className="drawer__body">
              <div className="error-box">{error || "Log not found."}</div>
            </div>
            <div className="drawer__footer">
              <button className="btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}

        {!loading && log && (
          <>
            <div className="drawer__body">
              {/* Status header */}
              <div className="event-id-box">
                <div>
                  <div className="event-id-box__label">Entity ID</div>
                  <div className="event-id-box__val">{log.entityId || "—"}</div>
                </div>
                <StatusBadge status={log.status} />
              </div>

              {/* Detail rows */}
              <div className="drawer-section-label">Event Details</div>
              {buildDetailRows(log).map(([k, v]) => (
                <div className="detail-row" key={k}>
                  <span className="detail-row__key">{k}</span>
                  <span className="detail-row__val">{v}</span>
                </div>
              ))}

              {/* Status message */}
              <div className="drawer-section-label">Status Details</div>
              {isError ? (
                <div className="error-box">
                  <strong>Error:</strong> {log.detail}
                </div>
              ) : (
                <div className="success-box">✓ {log.detail}</div>
              )}

              {/* Media info — only if fileSize present */}
              {log.fileSize && (
                <>
                  <div className="drawer-section-label">Media Info</div>
                  <div className="media-info-box">
                    <div className="media-info-box__row">
                      <span className="media-info-box__label">File Size</span>
                      <span className="media-info-box__val">
                        {formatFileSize(log.fileSize)}
                      </span>
                    </div>
                    <div className="media-info-box__row">
                      <span className="media-info-box__label">Type</span>
                      <TypeTag type={log.messageType} />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="drawer__footer">
              {log.status === "FAILED" && (
                <button
                  className="btn-danger"
                  onClick={() => {
                    onRetry(log.id);
                    onClose();
                  }}
                >
                  🔄 Retry Delivery
                </button>
              )}
              <button className="btn-primary" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Routing() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selectedLogId, setSelectedLogId] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [toast, setToast] = useState(null);
  const pageSize = 20;

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  const fetchLogs = useCallback(() => {
    setLoading(true);
    getWhatsappLogsApi(page, pageSize, statusFilter, typeFilter, search)
      .then((res) => {
        setLogs(res.data.logs || []);
        setPagination(res.data.pagination || { total: 0, totalPages: 1 });
      })
      .catch(() => showToast("❌ Failed to load logs"))
      .finally(() => setLoading(false));
  }, [page, pageSize, statusFilter, typeFilter, search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleRetry = (id) => {
    retryWhatsappLogApi(id)
      .then(() => {
        showToast("🔄 Retry successful");
        fetchLogs();
      })
      .catch((err) => {
        const msg = err.response?.data?.message || "Retry failed";
        showToast(`❌ ${msg}`);
      });
  };

  // KPI counts from current page (for live feel)
  const kpiItems = [
    {
      label: "Total Events",
      value: pagination.total,
      trend: "All time",
      icon: "📊",
      pos: true,
    },
    {
      label: "Routed",
      value: logs.filter((l) => l.status === "ROUTED" || l.status === "SUCCESS")
        .length,
      trend: "This page",
      icon: "✅",
      pos: true,
    },
    {
      label: "Failed",
      value: logs.filter((l) => l.status === "FAILED").length,
      trend: "Needs retry",
      icon: "❌",
      pos: false,
    },
    {
      label: "Rejected",
      value: logs.filter((l) => l.status === "REJECTED").length,
      trend: "No mapping found",
      icon: "⊘",
      pos: false,
    },
  ];

  return (
    <div className="routing-page">
      {/* Header */}
      <div className="routing-header">
        <div>
          <h1 className="routing-header__title">WhatsApp Routing Logs</h1>
          <p className="routing-header__desc">
            Review all inbound WhatsApp routing activity and troubleshoot
            delivery failures.
          </p>
        </div>
        <div className="routing-header__actions">
          <button className="btn-primary">📤 Export CSV</button>
        </div>
      </div>

      <div className="routing-content">
        {/* KPI Row */}
        <div className="kpi-row">
          {kpiItems.map((k) => (
            <div className="kpi-card" key={k.label}>
              <div className="kpi-card__header">
                <span className="kpi-card__label">{k.label}</span>
                <span className="kpi-card__icon">{k.icon}</span>
              </div>
              <div className="kpi-card__value">{k.value}</div>
              <span
                className={`kpi-card__trend kpi-card__trend--${k.pos ? "pos" : "neg"}`}
              >
                {k.trend}
              </span>
            </div>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="filter-bar">
          <div className="filter-group">
            <span className="filter-label">Search</span>
            <input
              className="filter-input"
              placeholder="Identity, action, detail…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <span className="filter-label">Type</span>
            <select
              className="filter-select"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Types</option>
              {MESSAGE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group filter-group--grow">
            <span className="filter-label">Status</span>
            <div className="status-chips">
              {STATUS_CHIPS.map((s) => (
                <button
                  key={s || "all"}
                  className={`chip chip--${statusFilter === s ? "active" : "inactive"}`}
                  onClick={() => {
                    setStatusFilter(s);
                    setPage(1);
                  }}
                >
                  {s === ""
                    ? `All (${pagination.total})`
                    : s.charAt(0) + s.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="table-wrap">
          <table className="routing-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Identity</th>
                <th>Action</th>
                <th>Matrix Room</th>
                <th>Type</th>
                <th>Size</th>
                <th>Status</th>
                <th>Detail</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="td-empty">
                    <div className="table-spinner" />
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="td-empty">
                    No logs found
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} onClick={() => setSelectedLogId(log.id)}>
                    <td className="td-time">{formatDate(log.createdAt)}</td>
                    <td>
                      <div className="td-from__number">
                        {log.whatsappIdentity || "—"}
                      </div>
                    </td>
                    <td>
                      <span className="td-action">{log.action}</span>
                    </td>
                    <td>
                      {log.matrixRoomId ? (
                        <span
                          className="td-room--link"
                          title={log.matrixRoomId}
                        >
                          {log.matrixRoomId.slice(0, 24)}…
                        </span>
                      ) : (
                        <span className="td-room--empty">—</span>
                      )}
                    </td>
                    <td>
                      <TypeTag type={log.messageType} />
                    </td>
                    <td className="td-duration">
                      {log.fileSize ? formatFileSize(log.fileSize) : "—"}
                    </td>
                    <td>
                      <StatusBadge status={log.status} />
                    </td>
                    <td>
                      <span
                        className={`td-detail td-detail--${
                          log.status === "FAILED" || log.status === "REJECTED"
                            ? "error"
                            : "normal"
                        }`}
                        title={log.detail}
                      >
                        {log.detail}
                      </span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="td-actions">
                        <button
                          className="btn-icon"
                          onClick={() => setSelectedLogId(log.id)}
                          title="View detail"
                        >
                          👁
                        </button>
                        {log.status === "FAILED" && (
                          <button
                            className="btn-icon btn-icon--warning"
                            onClick={() => handleRetry(log.id)}
                            title="Retry"
                          >
                            🔄
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            <span className="pagination__info">
              Showing {logs.length} of {pagination.total} events
            </span>
            <div className="pagination__controls">
              <div className="pagination__btns">
                <button
                  className="pagination__btn pagination__btn--inactive"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ‹
                </button>
                {Array.from({ length: pagination.totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`pagination__btn pagination__btn--${
                      page === i + 1 ? "active" : "inactive"
                    }`}
                    onClick={() => setPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="pagination__btn pagination__btn--inactive"
                  disabled={page === pagination.totalPages}
                  onClick={() =>
                    setPage((p) => Math.min(pagination.totalPages, p + 1))
                  }
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedLogId && (
        <LogDetailDrawer
          logId={selectedLogId}
          onClose={() => setSelectedLogId(null)}
          onRetry={handleRetry}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
